<script setup lang="ts">
import useMainStore from '~/shared/store'
import getSeoData from '~/composables/seo-data'
import getInitialData from '~/composables/initial-data'
import setVh from '~/composables/vh'
import NotificationsGroup from '~/components/ui/notifications/NotificationsGroup.vue'
import type { ISeoOptions } from '~/shared/interfaces/seo'
import { useConfigStore } from '~/shared/store/modules/config'
import AppButtonScrollTop from './components/ui/buttons/AppButtonScrollTop.vue'

const configStore = useConfigStore()
const mainStore = useMainStore()
const route = useRoute()
const pagesWithNoIndex = ['search-id', 'search-article-id']
let dataSeo = ref<ISeoOptions | null>()

watch(
  () => route.fullPath,
  async () => {
    const currentURL = useRequestURL()
    const seoUrl = configStore.config?.apiBaseUrl
      ? configStore.config.apiBaseUrl.replace('/v1', route.path)
      : currentURL.href
    dataSeo.value = await getSeoData(seoUrl)

    if (dataSeo.value) {
      dataSeo.value.type =
        route.name === 'search-product-id' ? 'product' : 'website'
      dataSeo.value.robots = pagesWithNoIndex.includes(String(route.name))
        ? 'noindex, follow'
        : dataSeo.value.robots
      dataSeo.value.url = currentURL.href.replace('http:', 'https:')
      mainStore.titleH1 = dataSeo.value?.h1 ? dataSeo.value.h1 : null
      mainStore.seoAdditionalText = dataSeo.value.additional_text
        ? dataSeo.value.additional_text
        : null
    }
    mainStore.titleH1 = dataSeo.value?.h1 ? dataSeo.value.h1 : null
    mainStore.seoAdditionalText = dataSeo?.value?.additional_text ? dataSeo.value.additional_text : null

  },
  { immediate: true }
)

useJsonld(
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Parts Sotrans',
    telephone: '8 (800) 200-02-33',
    email: 'support@sotrans.ru',
    logo: '',
    address: {
      '@type': 'PostalAddress',
      postalCode: '187015',
      addressRegion: 'Ленинградская область',
      addressLocality: 'пгт. Красный Бор',
      streetAddress: 'ул. Промышленная, 3',
      addressCountry: {
        '@type': 'Country',
        name: 'RU',
      },
    },
  },
  { tagPosition: 'head' }
)

useSeoMeta({
  title: () => `${dataSeo.value?.title || ''}`,
  description: () => `${dataSeo.value?.description || ''}`,
  ogTitle: () => `${dataSeo.value?.title || ''}`,
  ogDescription: () => `${dataSeo.value?.description || ''}`,
  ogImage: () => '',
  ogUrl: () => `${dataSeo.value?.url || ''}`,
  ogType: () => `${dataSeo.value?.type || 'website'}`,
  robots: () => dataSeo.value?.robots || 'index, follow',
  ogSiteName: 'Sotrans Parts',
  charset: 'utf-8',
  yandexVerification: '48eb7816fe0440bb',
  googleSiteVerification: 'uJFzNfkJnm3wSzk1Izb6BDtIkygXN1vEZ7yEF_9caRE',
})

useHead({
  bodyAttrs: {
    class: () => (mainStore.isBodyFixed ? 'fixed' : ''),
  },
  script: [
    // <!-- Yandex.Metrika counter -->
    {
      'data-cfasync': true,
      type: 'text/javascript',
      tagPosition: 'bodyClose',
      innerHTML:
        !import.meta.env.DEV && process.client
          ? `setTimeout(() => {
      (function (m, e, t, r, i, k, a) {
            m[i] = m[i] || function () {
                (m[i].a = m[i].a || []).push(arguments)
            };
            m[i].l = 1 * new Date();
            for (var j = 0; j < document.scripts.length; j++) {
                if (document.scripts[j].src === r) {
                    return;
                }
            }
            k = e.createElement(t), a = e.getElementsByTagName(t)[0], k.async = 1, k.src = r, a.parentNode.insertBefore(k, a)
        })
        (window, document, "script", "https://mc.yandex.ru/metrika/tag.js", "ym");

        ym(89928957, "init", {
            clickmap: true,
            trackLinks: true,
            accurateTrackBounce: true,
            webvisor: false
        });
        })`
          : '',
    },
  ],
  noscript: [
    {
      innerHTML: !import.meta.env.DEV
        ? `<div><img src="https://mc.yandex.ru/watch/89928957" style="position:absolute; left:-9999px;" alt=""/></div>`
        : '',
    },
    // <!-- /Yandex.Metrika counter -->
  ],
  link: [
    { rel: 'icon', type: 'image/x-icon', href: '/favicon.ico?v2' },
    {
      rel: 'canonical',
      href: `${useRequestURL().href.replace('http:', 'https:')}`,
    },
  ],
})

onMounted(() => {
  if (process.client) {
    setVh()
  }
})

const isBtnScrollUpVisible = computed(() => {
  return route?.name !== 'cart' && route?.name !== 'order'
})

await getInitialData()
</script>

<template>
  <div class="app-wrap">
    <NuxtLayout>
      <div>
        <NuxtPage />
      </div>
    </NuxtLayout>
    <NotificationsGroup />
    <AppButtonScrollTop :isVisible="isBtnScrollUpVisible" />
  </div>
</template>

<style lang="scss">
.app-wrap {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}
</style>
